import Button from 'components/Form/Button';
import TextAreaFieldWithAutoHeightResize from 'components/Form/TextAreaFieldWithAutoHeightResize';
import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  useAssignBankTrustee,
  useGetBankList
} from 'common/queries-and-mutations/bank';
import { Loader } from 'components/Loader';
import { toast } from 'react-toastify';
import { Alert } from 'components/Toast';
import { checkTrusteeAssignment, getTrusteeIds } from 'utils';

type Props = {
  onSuccess: () => void;
  trusteeId?: number;
};

export default function AssignBank({ trusteeId, onSuccess }: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: assets, isLoading: isAssetLoading } = useGetBankList();

  const { mutate, isSuccess, isError } = useAssignBankTrustee();

  // const existingId =
  //   assets?.filter(asset => asset.trusteeId === trusteeId).map(ass => ass.id) ??
  //   [];

  const formik = useFormik({
    initialValues: {
      trusteeId,
      note: '',
      selectedId: getTrusteeIds(assets!)
    },
    validationSchema: Yup.object({
      selectedId: Yup.array().min(1, 'Please select at least select on asset..')
    }),
    enableReinitialize: true,
    onSubmit: (values, { setFieldTouched }) => {
      setIsSubmitting(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      onSubmit(values);
    }
  });

  const onSubmit = (values: any) => {
    const payload = {
      trusteeId: values.trusteeId,
      note: values.note,
      assetId: values.selectedId
    };
    mutate(payload);
  };

  const handleSelectAsset = (assetId: number) => {
    if (!formik.values.selectedId.includes(assetId)) {
      const selectedId = [...formik.values.selectedId, assetId];
      formik.setFieldValue('selectedId', selectedId);
    } else {
      const selectedId = formik.values.selectedId.filter(
        value => value !== assetId
      );
      formik.setFieldValue('selectedId', selectedId);
    }
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      onSuccess();
      formik.resetForm();
      toast(<Alert message="Trustee Successfully Assigned" type="success" />);
      setIsSubmitting(false);
      formik.resetForm();
    }
    if (isError) {
      setIsSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, isError]);

  return (
    <div>
      {isAssetLoading ? (
        <div className="mt-40 flex flex-col items-center justify-center">
          <Loader color={'#FF6606'} height={50} width={50} />
        </div>
      ) : (
        <form onSubmit={formik.handleSubmit}>
          <div className="my-10 flex flex-wrap items-center gap-2">
            {assets?.map(asset => (
              <div
                className="flex h-20 min-w-[12rem] max-w-xs flex-col justify-center rounded-[5px] border border-[#A0B3FF] bg-[#F9FAFF] p-3"
                key={asset.id}
              >
                <div className="flex items-start gap-5">
                  <div className="flex items-center gap-5">
                    <input
                      defaultChecked={formik.values.selectedId?.includes(
                        asset.id!
                      )}
                      checked={formik.values.selectedId?.includes(asset.id!)}
                      disabled={
                        checkTrusteeAssignment(asset.trustee, trusteeId!)
                          .shouldDisabled
                      }
                      onChange={() => handleSelectAsset(asset.id!)}
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 bg-gray-100 text-[#171DFF] focus:ring-2 focus:ring-[#7B95FF]"
                    />
                    <div className="flex flex-col">
                      <p className="max-w-[9rem] truncate text-[13px] font-medium text-[#34475E]">
                        {asset.bankName}{' '}
                      </p>
                      <p className="text-xs font-normal text-[#34475E]">
                        {asset.accountNumber}
                      </p>
                      {checkTrusteeAssignment(asset.trustee, trusteeId!)
                        .alreadyAssignedToYou && (
                        <p className="mt-1 text-[10px] font-normal italic text-[#FF6606]">
                          Already Assigned to this trustee
                        </p>
                      )}
                      {checkTrusteeAssignment(asset.trustee, trusteeId!)
                        .assignedToAnotherUser && (
                        <p className="mt-1 text-[10px] font-normal italic text-[#FF6606]">
                          Already Assigned to another trustee
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="w-2/4">
            <TextAreaFieldWithAutoHeightResize
              value={formik.values.note}
              name="note"
              placeholder="Add comments..."
              label=""
              size="md"
              className="mt-10 rounded-md border-[1.5px] border-[#E1E1E1]"
              onChange={formik.handleChange}
            />
          </div>
          <Button
            type="submit"
            variant="primary"
            size="custom"
            className={`my-10 w-[12rem] rounded border py-2.5 font-semibold`}
            label="Assign Asset"
            disabled={isSubmitting}
            loading={isSubmitting}
          ></Button>
        </form>
      )}
    </div>
  );
}
